import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiAward,
  FiBookOpen,
  FiCheckCircle,
  FiMail,
  FiSettings,
  FiUsers,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import TrainingStandardsSection from "./TrainingStandardsSection";
import { TRAINING_PAGE_STANDARDS } from "../../data/trainingData";

const SITE_URL = "https://mbasolutions.co";

const pecbPrograms = [
  {
    title: "PECB ISO Lead Auditor",
    icon: FiBookOpen,
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978",
    description:
      "Internationally recognized Lead Auditor training enabling professionals to audit management systems effectively in accordance with ISO standards.",
    points: [
      "Audit principles & techniques",
      "ISO conformity assessment",
      "Audit planning & execution",
      "Reporting & corrective actions",
    ],
  },
  {
    title: "PECB ISO Lead Implementer",
    icon: FiSettings,
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d",
    description:
      "Designed for professionals responsible for implementing, maintaining, and improving management systems within organizations.",
    points: [
      "Management system framework",
      "Implementation roadmap",
      "Risk-based thinking",
      "Continual improvement",
    ],
  },
  {
    title: "PECB Foundation & Transition",
    icon: FiUsers,
    image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644",
    description:
      "Foundation and transition trainings provide essential understanding of ISO standards and updates for professionals at all levels.",
    points: [
      "ISO standards overview",
      "Key clauses & requirements",
      "Version transition guidance",
      "Practical case studies",
    ],
  },
];

const PecbTraining = () => {
  return (
    <Layout
      title="PECB Certified Professional Trainings | MBA Solutions"
      description="MBA Solutions delivers official PECB-certified trainings empowering professionals with globally recognized credentials and practical skills."
      keywords="PECB training, PECB Lead Auditor, PECB Lead Implementer, ISO training, MBA Solutions PECB"
      canonical={`${SITE_URL}/pecb-training`}
    >
      <TrainingHero
        badge="PECB Certified Partner"
        title="PECB Certified Professional Trainings"
        subtitle="Become a globally certified professional with official PECB trainings delivered by MBA Solutions."
        icon={FiAward}
        primaryCta={{ label: "Enroll in PECB Training", to: "/contact" }}
        secondaryCta={{
          label: "ISO Lead Auditor Hub",
          to: "/iso-lead-auditor-training",
        }}
      />

      <TrainingStandardsSection
        {...TRAINING_PAGE_STANDARDS.informationSecurity}
      />

      <section className="bg-white py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--bg-primary)] mb-4">
              PECB Certified Professional Trainings
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg">
              MBA Solutions delivers official PECB-certified trainings empowering
              professionals with globally recognized credentials and practical
              skills.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {pecbPrograms.map((program, index) => {
              const Icon = program.icon;
              return (
                <motion.div
                  key={program.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  whileHover={{ y: -12 }}
                  className="group bg-gray-50 rounded-3xl overflow-hidden shadow-xl border border-gray-100"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"
                      aria-hidden="true"
                    />
                    <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                      <Icon size={22} aria-hidden="true" />
                      <span className="font-semibold">PECB</span>
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                      {program.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">
                      {program.description}
                    </p>
                    <ul className="space-y-3 text-sm text-gray-700 list-none p-0 m-0">
                      {program.points.map((point) => (
                        <li key={point} className="flex items-start gap-2">
                          <FiCheckCircle
                            size={16}
                            className="text-[var(--bg-primary)] mt-1 shrink-0"
                            aria-hidden="true"
                          />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 text-center"
          >
            <p className="text-gray-600 mb-6">
              Become a globally certified professional with PECB trainings.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
              >
                <FiMail size={18} aria-hidden="true" /> Enroll in PECB Training
              </Link>
              <Link
                to="/training"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[var(--bg-primary)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--bg-primary)] hover:text-white transition-all"
              >
                View Scheduled Programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default PecbTraining;
